import React, { useState, useEffect } from 'react'
import { FaBold, FaItalic, FaLink, FaMagic, FaSpinner } from 'react-icons/fa'
import '../../styles/components/guide/RichTextToolbar.css'
import { toast } from 'react-toastify'
import { getDisplayLength } from '../common/TextValidator'
import useApi from '../../Api'
import { useAuth } from '../../AuthContext'

const RichTextToolbar = ({ editorRef, textValue, onChange, maxLength, isAdmin = false }) => {
  const api = useApi()
  const { token } = useAuth()

  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkText, setLinkText] = useState('')
  const [isBoldActive, setIsBoldActive] = useState(false)
  const [isItalicActive, setIsItalicActive] = useState(false)

  const [stylePrompt, setStylePrompt] = useState(() => sessionStorage.getItem('stylePrompt') || '')
  useEffect(() => {
    sessionStorage.setItem('stylePrompt', stylePrompt)
  }, [stylePrompt])

  const [showRefine, setShowRefine] = useState(false)
  const [refining, setRefining] = useState(false)
  const [refineError, setRefineError] = useState('')
  const [refinedOutput, setRefinedOutput] = useState('')

  const focusEditor = () => editorRef.current?.focus()
  const getEditorHtml = () => editorRef.current?.innerHTML || ''

  const applyFormat = (cmd, val = null) => {
    focusEditor()
    document.execCommand(cmd, false, val)
    const html = getEditorHtml()
    if (getDisplayLength(html) <= maxLength) {
      onChange(html)
    } else {
      document.execCommand('undo')
      toast.error('Exceeded maximum length.')
    }
  }

  const handleBold = () => applyFormat('bold')
  const handleItalic = () => applyFormat('italic')
  const handleLink = () => setShowLinkModal(true)

  const handleInsertLink = () => {
    if (!linkUrl.trim() || !linkText.trim()) {
      toast.error('Please provide both URL and text.')
      return
    }
    applyFormat('createLink', linkUrl.trim())
    const sel = window.getSelection()
    if (sel.rangeCount) {
      const anchor = sel.getRangeAt(0).startContainer.parentElement
      if (anchor.tagName === 'A') anchor.textContent = linkText.trim()
    }
    setShowLinkModal(false)
    setLinkUrl('')
    setLinkText('')
  }

  const handleRefine = async () => {
    if (!stylePrompt.trim()) {
      setRefineError('Please enter a styling prompt.')
      return
    }
    setRefineError('')
    setRefining(true)
    try {
      const res = await api.post(
        '/admin/refine-content',
        {
          style_prompt: stylePrompt,
          existing_content: getEditorHtml(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setRefinedOutput(res.data.updated_content)
    } catch (err) {
      setRefineError(err.response?.data?.detail || 'Refine failed.')
    } finally {
      setRefining(false)
    }
  }

  useEffect(() => {
    const onSelect = () => {
      if (!editorRef.current?.contains(document.activeElement)) return
      setIsBoldActive(document.queryCommandState('bold'))
      setIsItalicActive(document.queryCommandState('italic'))
    }
    document.addEventListener('selectionchange', onSelect)
    return () => document.removeEventListener('selectionchange', onSelect)
  }, [editorRef])

  return (
    <div className="rich-toolbar-wrapper">
      <div className="text-toolbar">
        <button type="button" className={isBoldActive ? 'active' : ''} onClick={handleBold} aria-label="Bold">
          <FaBold />
        </button>
        <button type="button" className={isItalicActive ? 'active' : ''} onClick={handleItalic} aria-label="Italic">
          <FaItalic />
        </button>
        <button type="button" className={showLinkModal ? 'active' : ''} onClick={handleLink} aria-label="Insert Link">
          <FaLink />
        </button>
        {isAdmin && (
          <button
            type="button"
            className={showRefine ? 'active' : ''}
            onClick={() => setShowRefine(v => !v)}
            aria-label="Refine style"
          >
            <FaMagic />
          </button>
        )}
      </div>

      {showLinkModal && (
        <div className="link-modal-overlay">
          <div className="link-modal">
            <h2>Insert Link</h2>
            <label>URL:</label>
            <input
              type="text"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
            />
            <label>Link Text:</label>
            <input
              type="text"
              value={linkText}
              onChange={e => setLinkText(e.target.value)}
              placeholder="Display text"
            />
            <div className="link-modal-actions">
              <button onClick={handleInsertLink}>Insert</button>
              <button onClick={() => setShowLinkModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showRefine && (
        <div className="refine-container">
          <label>Styling Instructions:</label>
          <textarea
            rows={3}
            value={stylePrompt}
            onChange={e => setStylePrompt(e.target.value)}
            placeholder="e.g. “Use a lively, conversational tone…”"
          />
          {refineError && <div className="error-text">{refineError}</div>}
          <button onClick={handleRefine} disabled={refining}>
            {refining ? <FaSpinner className="spin" /> : 'Apply Style'}
          </button>
          {refinedOutput && (
            <div className="refine-output-container">
              <label>Refined Content (read-only):</label>
              <textarea
                readOnly
                rows={8}
                value={refinedOutput}
                onFocus={e => e.target.select()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default RichTextToolbar
